// src/components/sla/ai/SlaAiInbox.js
// SLA AI notifications for the signed-in user (supervisors also see the team-wide ones).
// `variant="bell"` renders a header bell with an unread count and a popover list.
import React, { memo, useCallback } from 'react';
import {
    Badge, Box, Button, Flex, HStack, Icon, IconButton, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverHeader,
    PopoverTrigger, Skeleton, Stack, Text, useColorModeValue,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdDoneAll, MdNotifications } from 'react-icons/md';
import { useSlaAiInbox } from '../../../hooks/useSlaAi';
import { fmtRelative } from './slaAiFormat';

const SEVERITY = { critical: 'red', warning: 'orange', info: 'blue' };

const InboxList = memo(function InboxList({ inbox, onOpenDecision, max = 30 }) {
    const unreadBg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const { markRead } = inbox;
    const click = useCallback((n) => {
        if (!n.read_at) markRead(n.id).catch(() => {});
        if (n.decision_id && onOpenDecision) onOpenDecision({ id: n.decision_id, work_order_id: n.work_order_id });
    }, [markRead, onOpenDecision]);
    if (inbox.loading) return <Stack>{[0, 1, 2].map(i => <Skeleton key={i} h="52px" borderRadius="md" />)}</Stack>;
    if (inbox.error) return <Text fontSize="sm" color="red.400">{inbox.error}</Text>;
    if (!inbox.rows.length) return <Text fontSize="sm" color={muted} py={4} textAlign="center">No SLA AI notifications.</Text>;
    return (
        <Stack spacing={1} maxH="420px" overflowY="auto">
            {inbox.rows.slice(0, max).map(n => (
                <Box key={n.id} as="button" type="button" textAlign="left" p={2} borderRadius="md" bg={n.read_at ? undefined : unreadBg} onClick={() => click(n)} _hover={{ bg: unreadBg }}>
                    <HStack spacing={2} align="start">
                        <Box w="8px" h="8px" mt="6px" borderRadius="full" bg={`${SEVERITY[n.severity] || 'blue'}.400`} flexShrink={0} />
                        <Box flex="1" minW={0}>
                            <Text fontSize="sm" fontWeight={n.read_at ? 500 : 700} noOfLines={1}>{n.title}</Text>
                            {n.body && <Text fontSize="xs" color={muted} noOfLines={2}>{n.body}</Text>}
                            <HStack spacing={2} mt={0.5}>
                                <Text fontSize="2xs" color={muted}>{fmtRelative(n.created_at)}</Text>
                                {n.audience === 'supervisor' && <Badge fontSize="2xs" variant="subtle">team</Badge>}
                            </HStack>
                        </Box>
                    </HStack>
                </Box>
            ))}
        </Stack>
    );
});

function SlaAiInbox({ variant = 'panel', onOpenDecision }) {
    const inbox = useSlaAiInbox({ limit: 40 });
    const muted = useColorModeValue('gray.500', 'gray.400');
    const header = (
        <Flex justify="space-between" align="center">
            <HStack><Icon as={MdAutoAwesome} color="purple.500" /><Text fontWeight="800">SLA AI inbox</Text>{inbox.unread > 0 && <Badge colorScheme="red">{inbox.unread} new</Badge>}</HStack>
            <Button size="xs" variant="ghost" leftIcon={<MdDoneAll />} onClick={() => inbox.markRead('all').catch(() => {})} isDisabled={!inbox.unread}>Mark all read</Button>
        </Flex>
    );
    if (variant === 'bell') {
        return (
            <Popover placement="bottom-end" isLazy>
                <PopoverTrigger>
                    <Box position="relative" display="inline-block">
                        <IconButton icon={<MdNotifications />} variant="ghost" aria-label={`SLA AI notifications${inbox.unread ? `, ${inbox.unread} unread` : ''}`} />
                        {inbox.unread > 0 && <Badge colorScheme="red" borderRadius="full" position="absolute" top="2px" right="2px" fontSize="2xs" pointerEvents="none">{inbox.unread > 99 ? '99+' : inbox.unread}</Badge>}
                    </Box>
                </PopoverTrigger>
                <PopoverContent w="380px">
                    <PopoverArrow />
                    <PopoverHeader>{header}</PopoverHeader>
                    <PopoverBody><InboxList inbox={inbox} onOpenDecision={onOpenDecision} max={15} /></PopoverBody>
                </PopoverContent>
            </Popover>
        );
    }
    return (
        <Box>
            <Box mb={3}>{header}</Box>
            <InboxList inbox={inbox} onOpenDecision={onOpenDecision} />
            <Text fontSize="xs" color={muted} mt={2}>Technicians also receive these as push notifications in the tech app.</Text>
        </Box>
    );
}

export default memo(SlaAiInbox);
